import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building2, Save, Loader2, FileText, Calendar, Briefcase, Hash, Upload, Image, X, Camera } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTEURS_ACTIVITE = [
  { value: 'commerce', label: 'Commerce' },
  { value: 'artisanat', label: 'Artisanat' },
  { value: 'services', label: 'Services' },
  { value: 'liberal', label: 'Profession libérale' },
  { value: 'batiment', label: 'Bâtiment' },
  { value: 'transport', label: 'Transport' },
  { value: 'restauration', label: 'Restauration' },
  { value: 'conseil', label: 'Conseil' },
  { value: 'formation', label: 'Formation' },
  { value: 'informatique', label: 'Informatique / Digital' },
  { value: 'autre', label: 'Autre' }
];

function MonEntreprisePage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [formData, setFormData] = useState({
    nom_entreprise: '',
    siret: '',
    date_debut_activite: '',
    secteur_activite: ''
  });
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  const [logoUrl, setLogoUrl] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(function() {
    fetchSettings();
  }, []);

  async function fetchSettings() {
    try {
      const response = await axios.get(API_URL + '/api/settings', {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      const data = response.data;
      setUserInfo({
        name: data.user?.name || '',
        email: data.user?.email || ''
      });
      
      setFormData({
        nom_entreprise: data.business?.nom_entreprise || '',
        siret: data.business?.siret || '',
        date_debut_activite: data.business?.date_debut_activite || '',
        secteur_activite: data.business?.secteur_activite || ''
      });

      // Récupérer le logo
      if (data.business?.logo_url) {
        setLogoUrl(API_URL + data.business.logo_url);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }

  function handleChange(field, value) {
    setFormData(function(prev) {
      return { ...prev, [field]: value };
    });
  }

  async function handleLogoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Vérifier le type de fichier
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez PNG, JPG ou WebP.');
      return;
    }

    // Vérifier la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux. Maximum 5 Mo.');
      return;
    }

    setUploadingLogo(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('logo', file);

      const response = await axios.post(
        API_URL + '/api/entreprise/logo',
        formDataUpload,
        {
          headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setLogoUrl(API_URL + response.data.logo_url + '?t=' + Date.now());
      toast.success('Logo uploadé avec succès');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload du logo');
    } finally {
      setUploadingLogo(false);
    }
  }

  async function handleDeleteLogo() {
    try {
      await axios.delete(API_URL + '/api/entreprise/logo', {
        headers: { Authorization: 'Bearer ' + token }
      });
      setLogoUrl(null);
      toast.success('Logo supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(
        API_URL + '/api/settings',
        { business: formData },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      toast.success('Informations enregistrées avec succès');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  }

  function formatSiret(value) {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return digits.slice(0, 3) + ' ' + digits.slice(3);
    if (digits.length <= 9) return digits.slice(0, 3) + ' ' + digits.slice(3, 6) + ' ' + digits.slice(6);
    return digits.slice(0, 3) + ' ' + digits.slice(3, 6) + ' ' + digits.slice(6, 9) + ' ' + digits.slice(9, 14);
  }

  function handleSiretChange(e) {
    const formatted = formatSiret(e.target.value);
    if (formatted.replace(/\s/g, '').length <= 14) {
      handleChange('siret', formatted);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mon-entreprise-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Mon entreprise</h1>
        <p className="text-slate-500 mt-1">Gérez les informations de votre activité professionnelle</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6 md:grid-cols-2">
          
          {/* Logo de l'entreprise */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="h-5 w-5 text-blue-600" />
                Logo / Photo de l&apos;entreprise
              </CardTitle>
              <CardDescription>
                Ce logo apparaîtra sur vos factures et documents exportés
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                {/* Logo Preview */}
                <div className="relative">
                  <div className="h-32 w-32 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center bg-slate-50 overflow-hidden">
                    {logoUrl ? (
                      <img 
                        src={logoUrl} 
                        alt="Logo entreprise" 
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <div className="text-center p-4">
                        <Image className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                        <p className="text-xs text-slate-500">Aucun logo</p>
                      </div>
                    )}
                  </div>
                  {logoUrl && (
                    <button
                      type="button"
                      onClick={handleDeleteLogo}
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* Upload Section */}
                <div className="flex-1">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleLogoUpload}
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={function() { fileInputRef.current?.click(); }}
                    disabled={uploadingLogo}
                    className="mb-3"
                  >
                    {uploadingLogo ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Upload en cours...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        {logoUrl ? 'Changer le logo' : 'Ajouter un logo'}
                      </>
                    )}
                  </Button>
                  <p className="text-sm text-slate-500">
                    Formats acceptés : PNG, JPG, WebP
                  </p>
                  <p className="text-xs text-slate-400">
                    Taille maximale : 5 Mo · Dimensions recommandées : 512x512 px
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Informations principales */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-blue-600" />
                Informations de l&apos;entreprise
              </CardTitle>
              <CardDescription>
                Ces informations apparaîtront sur vos documents exportés
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nom_entreprise" className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-slate-400" />
                  Nom de l&apos;entreprise *
                </Label>
                <Input
                  id="nom_entreprise"
                  value={formData.nom_entreprise}
                  onChange={function(e) { handleChange('nom_entreprise', e.target.value); }}
                  placeholder="Ma Micro-Entreprise"
                  data-testid="input-nom-entreprise"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="siret" className="flex items-center gap-2">
                  <Hash className="h-4 w-4 text-slate-400" />
                  Numéro SIRET
                </Label>
                <Input
                  id="siret"
                  value={formData.siret}
                  onChange={handleSiretChange}
                  placeholder="123 456 789 00012"
                  maxLength={17}
                  data-testid="input-siret"
                />
                <p className="text-xs text-slate-500">
                  14 chiffres - Disponible sur votre avis de situation INSEE
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Informations complémentaires */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Détails de l&apos;activité
              </CardTitle>
              <CardDescription>
                Informations utiles pour le suivi de votre activité
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="date_debut" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  Date de début d&apos;activité
                </Label>
                <Input
                  id="date_debut"
                  type="date"
                  value={formData.date_debut_activite}
                  onChange={function(e) { handleChange('date_debut_activite', e.target.value); }}
                  data-testid="input-date-debut"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="secteur" className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-slate-400" />
                  Secteur d&apos;activité
                </Label>
                <Select 
                  value={formData.secteur_activite} 
                  onValueChange={function(value) { handleChange('secteur_activite', value); }}
                >
                  <SelectTrigger id="secteur" data-testid="select-secteur">
                    <SelectValue placeholder="Sélectionnez un secteur" />
                  </SelectTrigger>
                  <SelectContent>
                    {SECTEURS_ACTIVITE.map(function(secteur) {
                      return (
                        <SelectItem key={secteur.value} value={secteur.value}>
                          {secteur.label}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Informations du compte */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Informations du compte</CardTitle>
              <CardDescription>
                Ces informations proviennent de votre inscription
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-slate-500">Titulaire du compte</Label>
                  <p className="text-slate-900 font-medium">{userInfo.name || '-'}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-500">Adresse email</Label>
                  <p className="text-slate-900 font-medium">{userInfo.email || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Submit button */}
        <div className="flex justify-end mt-6">
          <Button 
            type="submit" 
            disabled={saving}
            className="min-w-[200px]"
            data-testid="save-entreprise-btn"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Enregistrer les modifications
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default MonEntreprisePage;
